import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "../api/client";
import API_ENDPOINTS from "../constants/api-endpoints";
import {
  TCustomFieldDefinition,
  TCustomFieldValue,
} from "../types/customFields";

type SaveCallbacks = {
  onSuccess?: (res: any) => void;
  onError?: (err: any) => void;
};

export type DefinitionListParams = {
  search?: string;
  company_id?: number;
  entity_type?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
};

export type ValueListParams = {
  company_id?: number;
  entity_type?: string;
  entity_id?: number;
  definition_id?: number;
  page?: number;
  limit?: number;
};

export type TDefinitionListResult = {
  data: TCustomFieldDefinition[];
  total: number;
  page: number;
  limit: number;
};

export type TValueListResult = {
  data: TCustomFieldValue[];
  total: number;
  page: number;
  limit: number;
};

// ─── DEFINITIONS ─────────────────────────────────────────────────────────────

export const useCustomFieldDefinitionList = (
  params: DefinitionListParams = { page: 1, limit: 20 },
) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.CUSTOM_FIELD_DEFINITIONS, params],
    queryFn: () =>
      client.get(API_ENDPOINTS.CUSTOM_FIELD_DEFINITIONS, { params }),
    select: (res: any): TDefinitionListResult => ({
      data: res.data.data as TCustomFieldDefinition[],
      total: res.data.extra?.total ?? 0,
      page: res.data.extra?.page ?? 1,
      limit: res.data.extra?.limit ?? 20,
    }),
    refetchOnWindowFocus: false,
  });
};

export const useCustomFieldDefinitionCreate = ({
  onSuccess,
  onError,
}: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<TCustomFieldDefinition>) =>
      client.post(API_ENDPOINTS.CUSTOM_FIELD_DEFINITIONS, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.CUSTOM_FIELD_DEFINITIONS],
      });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCustomFieldDefinitionUpdate = ({
  onSuccess,
  onError,
}: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<TCustomFieldDefinition>;
    }) => client.put(API_ENDPOINTS.CUSTOM_FIELD_DEFINITION(id), data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.CUSTOM_FIELD_DEFINITIONS],
      });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCustomFieldDefinitionDelete = ({
  onSuccess,
  onError,
}: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      client.delete(API_ENDPOINTS.CUSTOM_FIELD_DEFINITION(id)),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.CUSTOM_FIELD_DEFINITIONS],
      });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

// ─── VALUES ──────────────────────────────────────────────────────────────────

export const useCustomFieldValueList = (
  params: ValueListParams = { page: 1, limit: 20 },
) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.CUSTOM_FIELD_VALUES, params],
    queryFn: () => client.get(API_ENDPOINTS.CUSTOM_FIELD_VALUES, { params }),
    select: (res: any): TValueListResult => ({
      data: res.data.data as TCustomFieldValue[],
      total: res.data.extra?.total ?? 0,
      page: res.data.extra?.page ?? 1,
      limit: res.data.extra?.limit ?? 20,
    }),
    refetchOnWindowFocus: false,
  });
};

export const useCustomFieldValueUpsert = ({
  onSuccess,
  onError,
}: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<TCustomFieldValue>) =>
      client.post(API_ENDPOINTS.CUSTOM_FIELD_VALUES, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.CUSTOM_FIELD_VALUES],
      });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCustomFieldValueDelete = ({
  onSuccess,
  onError,
}: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      client.delete(API_ENDPOINTS.CUSTOM_FIELD_VALUE(id)),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.CUSTOM_FIELD_VALUES],
      });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};
